import { Badge, Button, Tab, Tabs } from "react-bootstrap";
import { navigateTo } from "../../redux/router/routerActions";
import { useAppDispatch } from "../../redux/store";
import Collapsible from "../Collapsible/Collapsible";
import JSONData from "../JSONData/JSONData";
import VrboListingSection from "./sections/VrboListingSection";
import VrboPromotionsSection from "./sections/VrboPromotionsSection";
import VrboRatesSection from "./sections/VrboRatesSection";
import VrboStatusSection from "./sections/VrboStatusSection";
import VrboVisibilitySection from "./sections/VrboVisibilitySection";
import { VrboIntegrationRecord } from "./useVrboIntegrationRecord";

function formatDateTime(value?: string | Date | null) {
    if (!value) return 'Never'
    const date = new Date(value)
    return isNaN(date.getTime()) ? 'Unknown' : date.toLocaleString()
}

export default function VrboConnected(
    props: {
        propertyId: string
        record: VrboIntegrationRecord
    }
) {
    const { propertyId, record } = props
    const {
        integrationData,
        vrboPropertyData,
        setVrboPropertyData,
        vrboQueryErrorData,
        setVrboQueryErrorData,
        isQueryingVRBO,
        handleQueryVRBOProperty,
        updateIntegrationData,
        markSynced,
        isUpdatingIntegration,
        updateIntegrationError,
    } = record

    const dispatch = useAppDispatch()

    return (
        <div>
            <div className="border rounded p-3 mb-3">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                    <div>
                        <div>
                            <strong>VRBO Property ID:</strong>{' '}
                            <code>{integrationData.primaryExternalId || 'Unknown'}</code>
                        </div>
                        <div className="d-flex align-items-center gap-2 mt-1">
                            <Badge bg="secondary">{integrationData.sync?.status || 'unknown'}</Badge>
                            <small className="text-muted">
                                Last synced: {formatDateTime(integrationData.sync?.lastSyncedAt)}
                            </small>
                        </div>
                    </div>
                    <div className="d-flex gap-2">
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={handleQueryVRBOProperty}
                            disabled={isQueryingVRBO}
                        >
                            {isQueryingVRBO ? 'Querying...' : 'Refresh from VRBO'}
                        </Button>
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => dispatch(navigateTo(`/delete-integration/${integrationData._id}`))}
                        >
                            Delete Integration
                        </Button>
                    </div>
                </div>
            </div>

            {vrboQueryErrorData && (
                <div className="mb-3">
                    <JSONData data={vrboQueryErrorData} title="Error querying VRBO property" onClose={() => setVrboQueryErrorData(null)} />
                </div>
            )}

            {vrboPropertyData && (
                <div className="mb-3">
                    <JSONData data={vrboPropertyData} title="Updated VRBO Data" onClose={() => setVrboPropertyData(null)} />
                    <Button className="mt-2" onClick={updateIntegrationData} disabled={isUpdatingIntegration}>
                        {isUpdatingIntegration ? 'Updating...' : 'Update Integration'}
                    </Button>
                    {updateIntegrationError && (
                        <JSONData data={updateIntegrationError} title="Error updating integration" />
                    )}
                </div>
            )}

            <Collapsible title="Integration Data" TitleTag="h6" openText="Show" closeText="Hide">
                <JSONData data={integrationData} collapsed={1} />
            </Collapsible>

            <Tabs defaultActiveKey="status" id="vrbo-integration-tabs" className="mb-3">
                <Tab eventKey="status" title="Status">
                    <VrboStatusSection propertyId={propertyId} />
                </Tab>
                <Tab eventKey="listing" title="Listing">
                    <VrboListingSection propertyId={propertyId} onSynced={markSynced} />
                </Tab>
                <Tab eventKey="rates" title="Rates & Policy">
                    <VrboRatesSection propertyId={propertyId} integrationData={integrationData} />
                </Tab>
                <Tab eventKey="promotions" title="Promotions" mountOnEnter>
                    <VrboPromotionsSection propertyId={propertyId} />
                </Tab>
                <Tab eventKey="visibility" title="Visibility">
                    <VrboVisibilitySection propertyId={propertyId} />
                </Tab>
            </Tabs>
        </div>
    )
}
